/**
 * Employee KPIs, scores and segments.
 *
 * Two scores rather than one composite, because they disagree and the
 * disagreement is the useful part. Performance is output against peers;
 * consistency is whether it arrives steadily. High performance with low
 * consistency is a lucky quarter; the reverse is someone reliably working the
 * wrong territory. An average of the two would hide both.
 *
 * The segment is a *style*, not a rank — a Workhorse is not a worse Specialist,
 * they are differently shaped contributions. Direction of travel lives on the
 * Employee comparison screen; this one is level and shape.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Info } from "lucide-react";

import { api } from "@/api/client";
import { KpiCard } from "@/components/cards/KpiCard";
import { DataTable, type Column } from "@/components/tables/DataTable";
import { count, inr, pct } from "@/utils/format";

interface Reason { direction: string; text: string; metric: string; value: unknown }

interface Row {
  person_key: string;
  name: string;
  segment: string;
  segment_label: string;
  performance: number | null;
  performance_band: string;
  consistency: number | null;
  consistency_band: string;
  suppressed: boolean;
  activities: number;
  activities_30d: number;
  admissions: number;
  conversion_pct: number | null;
  revenue: number;
  commission: number;
  clients_active: number;
  clients_dormant: number;
  top_client_share_pct: number | null;
  followup_compliance_pct: number | null;
  ratios_available: boolean;
  reasons: Reason[];
}

interface Segment { key: string; label: string; meaning: string; count: number }

const BAND_TONE: Record<string, string> = {
  Excellent: "#2F6B3C", Strong: "#0B7A5E", Average: "#8A6410",
  "Needs attention": "#B4690E", Critical: "#9E3B2E",
};

/** A 0-100 score with its band. Number and bar together, so a column scans. */
function ScoreCell({ value, band, suppressed }: {
  value: number | null; band: string; suppressed: boolean;
}) {
  if (value === null) return <span className="text-ink-faint">—</span>;
  const colour = BAND_TONE[band] ?? "#5D6E6A";
  return (
    <span className="inline-flex items-center gap-1.5 justify-end w-full"
          title={suppressed
            ? `${band} — indicative only, too few information items to rank`
            : band}>
      <span className="tnum text-[12.5px]"
            style={{ color: suppressed ? "#8A8F8C" : colour }}>
        {value.toFixed(0)}
      </span>
      <span className="w-10 h-1.5 rounded-full bg-surface-alt overflow-hidden shrink-0">
        <span className="block h-full rounded-full"
              style={{ width: `${value}%`,
                       background: colour,
                       opacity: suppressed ? 0.35 : 1 }} />
      </span>
    </span>
  );
}

export function EmployeeSegments() {
  const navigate = useNavigate();
  const [segment, setSegment] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["employee-scores", segment],
    queryFn: async () => {
      const { data } = await api.get<{
        results: Row[];
        summary: { employees: number; scored: number; segments: Segment[] };
      }>("/analytics/employees/scores", {
        params: { segment: segment || undefined },
      });
      return data;
    },
    staleTime: 300_000,
  });

  const rows = data?.results ?? [];
  const summary = data?.summary;

  const columns = useMemo<Column<Row>[]>(() => [
    {
      key: "name", header: "Person", width: "210px",
      render: (r) => (
        <span className="min-w-0">
          <span className="font-medium truncate block">{r.name}</span>
          <span className="text-[11px] text-ink-faint">{r.segment_label}</span>
        </span>
      ),
    },
    {
      key: "performance", header: "Performance", align: "right", width: "120px",
      render: (r) => <ScoreCell value={r.performance} band={r.performance_band}
                                suppressed={r.suppressed} />,
      value: (r) => r.performance,
    },
    {
      key: "consistency", header: "Consistency", align: "right", width: "120px",
      render: (r) => <ScoreCell value={r.consistency} band={r.consistency_band}
                                suppressed={r.suppressed} />,
      value: (r) => r.consistency,
    },
    {
      key: "revenue", header: "Revenue", align: "right",
      render: (r) => inr(r.revenue), value: (r) => r.revenue,
    },
    { key: "admissions", header: "Admissions", align: "right",
      value: (r) => r.admissions },
    {
      key: "conversion_pct", header: "Conversion", align: "right",
      render: (r) => (r.conversion_pct === null
        ? <span className="text-ink-faint" title="Under 20 information items">—</span>
        : pct(r.conversion_pct)),
      value: (r) => r.conversion_pct,
    },
    { key: "activities", header: "Activities", align: "right",
      value: (r) => r.activities },
    { key: "clients_active", header: "Active clients", align: "right",
      hidden: true, value: (r) => r.clients_active },
    {
      key: "top_client_share_pct", header: "Top-client share", align: "right",
      hidden: true,
      render: (r) => (r.top_client_share_pct === null ? "—"
        : <span className={r.top_client_share_pct >= 50 ? "text-warn" : ""}>
            {r.top_client_share_pct}%
          </span>),
      value: (r) => r.top_client_share_pct,
    },
    {
      key: "followup_compliance_pct", header: "Follow-up", align: "right",
      hidden: true, render: (r) => pct(r.followup_compliance_pct),
      value: (r) => r.followup_compliance_pct,
    },
  ], []);

  const activeSegment = summary?.segments.find((x) => x.key === segment);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display font-bold text-[22px] sm:text-[24px] tracking-tight">
          Employee KPIs and segments
        </h1>
        <p className="text-[13.5px] text-ink-muted mt-0.5 max-w-3xl">
          Two scores, kept apart on purpose. <strong>Performance</strong> is
          output against peers; <strong>consistency</strong> is whether it
          arrives every month or in one spike. Click a row for the reasons behind
          both.
        </p>
      </div>

      {isLoading && <div className="skeleton h-96" />}

      {!isLoading && summary && (
        <>
          <div className="grid gap-3"
               style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))" }}>
            <KpiCard label="People with activity" value={count(summary.employees)} />
            <KpiCard label="Rankable" value={count(summary.scored)}
                     footnote="20+ information items" />
            <KpiCard label="Not rankable"
                     value={count(summary.employees - summary.scored)}
                     higherIsBetter={false}
                     footnote="scores shown as indicative only" />
          </div>

          {/* Segments double as the filter. */}
          <div className="space-y-2">
            <div className="label">segment — a style, not a ranking</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setSegment("")}
                      className={`chip ${segment === "" ? "border-petrol text-petrol bg-petrol-soft/60" : ""}`}>
                All <span className="tnum opacity-70">{summary.employees}</span>
              </button>
              {summary.segments.filter((x) => x.count > 0).map((x) => (
                <button key={x.key} onClick={() => setSegment(x.key)}
                        title={x.meaning}
                        className={`chip ${segment === x.key
                          ? "border-petrol text-petrol bg-petrol-soft/60" : ""}`}>
                  {x.label} <span className="tnum opacity-70">{x.count}</span>
                </button>
              ))}
            </div>
            {activeSegment && (
              <p className="text-[12.5px] text-ink-muted">{activeSegment.meaning}</p>
            )}
          </div>

          <DataTable<Row>
            rows={rows} columns={columns} rowKey={(r) => r.person_key}
            onRowClick={(r) => setOpen(open === r.person_key ? null : r.person_key)}
            exportName="employee-scores"
            emptyMessage="No employees in this segment."
            footnote="Click a row for the components and reasons; hidden columns — active clients, top-client share, follow-up — are in the column picker."
          />

          {open && (() => {
            const row = rows.find((r) => r.person_key === open);
            if (!row) return null;
            return (
              <section className="card p-4 space-y-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <div className="label">why these scores</div>
                    <h2 className="font-display font-bold text-[17px]">{row.name}</h2>
                  </div>
                  <button className="btn text-xs"
                          onClick={() => navigate(`/employees/${encodeURIComponent(row.person_key)}`)}>
                    Open profile
                  </button>
                </div>

                {row.suppressed && (
                  <p className="flex items-start gap-2 text-[12.5px] text-warn">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                    Fewer than 20 information items recorded. Both scores are
                    shown but should be read as indicative — a percentile from
                    this little activity describes the sample, not the person.
                  </p>
                )}
                {!row.ratios_available && (
                  <p className="flex items-start gap-2 text-[12.5px] text-warn">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                    This person's activity ids and commission ids could not be
                    joined confidently, so effort-to-output ratios are withheld
                    rather than guessed.
                  </p>
                )}

                <ul className="space-y-1.5">
                  {row.reasons.map((reason, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px]">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                            style={{ background: reason.direction === "up" ? "#2F6B3C"
                              : reason.direction === "down" ? "#9E3B2E" : "#8A8F8C" }} />
                      <span>{reason.text}</span>
                    </li>
                  ))}
                  {row.reasons.length === 0 && (
                    <li className="text-[13px] text-ink-muted">
                      No reasons recorded for this person.
                    </li>
                  )}
                </ul>

                <p className="flex items-start gap-2 text-[12px] text-ink-faint">
                  <Info size={12} className="mt-0.5 shrink-0" />
                  Every score keeps its components with raw value, percentile and
                  weight. Nothing here is a black box, and nothing is a
                  prediction.
                </p>
              </section>
            );
          })()}
        </>
      )}
    </div>
  );
}
